<template>
  <section class="flex-col">
    <h2>Products</h2>
    <p class="subHeading">
      Explore opportunities to build wealth
    </p>

    <div class="content">
      <CommonPlanCard
        title="Bonds"
        :rate="bondsRate" 
        buttonText="Start with ₹10,000"
        :features="[
          { icon: 'mdi-circle', text: 'Listed on NSE' },
          { icon: 'mdi-circle', text: 'Rated by credit rating agencies' },
          { icon: 'mdi-circle', text: 'Receive & hold in demat form' },
          { icon: 'mdi-circle', text: 'Tradable on exchange' },
          // { icon: 'mdi-circle', text: 'Buy & sell anytime' },
        ]"
        buttonRedirectionTo="/products/bonds"
      />
      <CommonPlanCard
        title="Fixed Deposits"
        :rate="fdRate"
        buttonText="Start with ₹1,000"
        :isButtonDisable="false"
        :features="[
          { icon: 'mdi-circle', text: 'With Bank & Corporate FDs' },
          { icon: 'mdi-circle', text: 'No new bank account required' },
          { icon: 'mdi-circle', text: '7 days to 5 years tenure' },
        ]"
        :showIconSection="true"
        :iconList="[
            { imgSrc: '/icons/bajaj.svg', altText: 'bajajIcon' },
            { imgSrc: '/icons/shriram.svg', altText: 'shriramIcon' },
            { imgSrc: '/icons/unity.svg', altText: 'unityIcon' },
            { imgSrc: '/icons/suryoday.svg', altText: 'suryodayIcon' },
        ]"
        :showSuccessBadge="true"
        succesBadgeText="Bank FDs are insured up to ₹5L by RBI's DICGC"
        buttonRedirectionTo="/products/fixedDeposits"
      />
    </div> 
  </section>
</template>

<script setup lang="ts">
import { computed } from "vue";
import bondsService from "~/service/bondsService";
import fdService from "~/service/fdService";

const DEFAULT_BONDS_RATE = "9% to 14%";
const DEFAULT_FD_RATE = "8%";

const { data: ratesData } = await useAsyncData(
  "home-plans-rates",
  async () => {
    const [bondsHighestResult, bondsLowestResult, fdResult] = await Promise.allSettled([
      bondsService.getBondsListings({
        sortBy: "listed_yield",
        sortOrder: "DESC",
        limit: 1,
      }),
      bondsService.getBondsListings({
        sortBy: "listed_yield",
        sortOrder: "ASC",
        limit: 1,
      }),
      fdService.getFdSources(),
    ]);

    let calculatedBondsRate = DEFAULT_BONDS_RATE;
    if (
      bondsHighestResult.status === "fulfilled" &&
      bondsHighestResult.value &&
      bondsLowestResult.status === "fulfilled" &&
      bondsLowestResult.value
    ) {
      const topYield = Number((bondsHighestResult.value as any)?.listings?.[0]?.listedYield);
      const bottomYield = Number((bondsLowestResult.value as any)?.listings?.[0]?.listedYield);

      if (Number.isFinite(topYield) && topYield > 0 && Number.isFinite(bottomYield) && bottomYield > 0) {
        if (topYield === bottomYield) {
          calculatedBondsRate = `${Number.parseFloat(topYield.toFixed(2))}%`;
        } else {
          calculatedBondsRate = `${Number.parseFloat(bottomYield.toFixed(2))}% to ${Number.parseFloat(topYield.toFixed(2))}%`;
        }
      }
    }

    let calculatedFdRate = DEFAULT_FD_RATE;
    if (fdResult.status === "fulfilled" && fdResult.value) {
      const sources = (fdResult.value as any)?.sources;
      if (Array.isArray(sources) && sources.length > 0) {
        const maxInterest = Math.max(
          ...sources.map((s: any) => Number.parseFloat(s?.maxInterest || 0)),
          0
        );
        if (maxInterest > 0) {
          calculatedFdRate = `${Number.parseFloat(maxInterest.toFixed(2))}%`;
        }
      }
    }

    return {
      bondsRate: calculatedBondsRate,
      fdRate: calculatedFdRate,
    };
  },
  {
    server: true,
    default: () => ({
      bondsRate: DEFAULT_BONDS_RATE,
      fdRate: DEFAULT_FD_RATE,
    }),
  }
);

const bondsRate = computed(() => ratesData.value?.bondsRate || DEFAULT_BONDS_RATE);
const fdRate = computed(() => ratesData.value?.fdRate || DEFAULT_FD_RATE);
</script>

<style scoped>
.content {
  margin-top: 30px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 30px;
  width: 96%;
}

.img-fluid {
  width: 80%;
}

@media (min-width: 1024px) {
  .content {
    width: 60%;
    flex-direction: row;
    align-items: stretch;
    justify-content: center;
    gap: 100px;
  }

  .img-fluid {
    width: 400px;
  }
}
</style>
